import { lazy, Suspense } from 'react'
import { ProcessingIndicator } from './ProcessingIndicator'

const LazyToaster = lazy(() => import('sonner').then(({ Toaster }) => ({ default: Toaster })))
// App-owned shortcuts are authoritative; Sonner's implicit Alt+T conflicts with tags.
const TOAST_HOTKEY: string[] = []

const TOAST_OPTIONS = {
  unstyled: true,
  classNames: {
    toast: 'minimany-toast',
    content: 'minimany-toast__content',
    title: 'minimany-toast__title',
    description: 'minimany-toast__description',
    icon: 'minimany-toast__icon',
    actionButton: 'minimany-toast__action',
    cancelButton: 'minimany-toast__action',
    closeButton: 'minimany-toast__close',
    success: 'minimany-toast--success',
    error: 'minimany-toast--error',
  },
} as const

const MOBILE_TOAST_OFFSET = {
  bottom: 'calc(var(--visual-viewport-bottom-inset) + 0.75rem)',
} as const
// In the note editor a toast must never cover the formatting bar or the Save button (styles.css: --editor-toast-clearance).
const MOBILE_TOAST_OFFSET_WITH_FORMATTING = {
  bottom: 'calc(var(--visual-viewport-bottom-inset) + var(--editor-toast-clearance))',
} as const

type ToastOptions = Parameters<(typeof import('sonner'))['toast']>[1]
export type NotificationId = string | number

async function show(message: string, options?: ToastOptions): Promise<NotificationId> {
  const { toast } = await import('sonner')
  return toast(message, options)
}

export const notify = Object.assign(show, {
  async error(message: string, options?: ToastOptions): Promise<NotificationId> {
    const { toast } = await import('sonner')
    return toast.error(message, options)
  },
  async success(message: string, options?: ToastOptions): Promise<NotificationId> {
    const { toast } = await import('sonner')
    return toast.success(message, options)
  },
  async loading(message: string, options?: ToastOptions): Promise<NotificationId> {
    const { toast } = await import('sonner')
    return toast.loading(message, options)
  },
})

export function NotificationsViewport({ captureMode }: { captureMode: boolean }) {
  return (
    <Suspense fallback={null}>
      <LazyToaster
        hotkey={TOAST_HOTKEY}
        theme="dark"
        position="bottom-center"
        duration={4500}
        visibleToasts={3}
        gap={8}
        offset={captureMode ? '5.5rem' : '1rem'}
        mobileOffset={captureMode
          ? MOBILE_TOAST_OFFSET_WITH_FORMATTING
          : MOBILE_TOAST_OFFSET}
        toastOptions={TOAST_OPTIONS}
        icons={{ loading: <ProcessingIndicator /> }}
      />
    </Suspense>
  )
}
